import { useEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart3,
  Calendar,
  ChevronDown,
  FileText,
  GraduationCap,
  MessageSquare,
  Printer,
  Quote,
  Smile,
  Trophy,
} from 'lucide-react';

import { AwaitingResultsList } from '@/components/marks/AwaitingResultsList';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { gradeToneClasses } from '@/constants/grading';
import { cn } from '@/lib/utils';
import type { ParentResults } from '../types/results';
import {
  ParentReportCardsTab,
  ReportCardPrint,
  type ReportCardDetails,
} from './results/ParentReportCardsTab';
import {
  gradeScale,
  groupExams,
  isPraiseworthy,
  paperGrade,
  remarksOf,
} from './results/examGroups';
import { ParentUpcomingExamsTab } from './results/ParentUpcomingExamsTab';

// Subject bar colors and icons for the performance chart
function getSubjectChartColor(index: number): string {
  const colors = [
    'bg-[#8b5cf6]', // Purple (English)
    'bg-[#06b6d4]', // Cyan (Math)
    'bg-[#10b981]', // Emerald (EVS)
    'bg-[#f59e0b]', // Amber (Hindi)
    'bg-[#ec4899]', // Pink (Computer)
    'bg-[#6366f1]', // Indigo (Art)
    'bg-[#3b82f6]', // Blue
    'bg-[#14b8a6]', // Teal
  ];
  return colors[index % colors.length];
}

/** The shared grade scale, worked out once — it does not change between renders. */
const GRADE_SCALE = gradeScale();

interface ParentResultsReportProps {
  results: ParentResults | null;
  isLoading: boolean;
  error: { message: string } | null;
  onRetry: () => void;
  childName?: string;
  className?: string;
}

export function ParentResultsReport({
  results,
  isLoading,
  error,
  onRetry,
  childName,
  className,
}: ParentResultsReportProps) {
  const [activeTab, setActiveTab] = useState<'results' | 'upcoming' | 'reportCards'>('upcoming');
  const [isTermOpen, setIsTermOpen] = useState(false);
  const [printing, setPrinting] = useState<ReportCardDetails | null>(null);
  const termRef = useRef<HTMLDivElement>(null);

  const academicYearName = results?.academicYear?.name ?? '';

  // Close the exam picker on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (termRef.current && !termRef.current.contains(e.target as Node)) {
        setIsTermOpen(false);
      }
    }
    if (isTermOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isTermOpen]);

  const marksList = useMemo(() => results?.exams ?? [], [results]);

  // The child's exams, most recent first — the one place papers are grouped.
  const examGroups = useMemo(() => groupExams(marksList), [marksList]);

  // Active expanded exam
  const [activeExamName, setActiveExamName] = useState<string>('');
  const [expandedAccordionTerm, setExpandedAccordionTerm] = useState<string | null>(null);

  const currentActiveExam = useMemo(() => {
    if (examGroups.length === 0) return null;
    if (activeExamName) {
      const found = examGroups.find((g) => g.name === activeExamName);
      if (found) return found;
    }
    return examGroups[0];
  }, [examGroups, activeExamName]);

  // Every other exam this year, collapsed under the open one.
  const accordionTerms = useMemo(
    () => examGroups.filter((group) => group.name !== currentActiveExam?.name),
    [examGroups, currentActiveExam?.name],
  );

  // Today's formatted date
  const todayFormatted = useMemo(() => {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-GB', {
      // The school's day in India, not the device's zone.
      timeZone: 'Asia/Kolkata',
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    return `Today is ${formatter.format(now)}`;
  }, []);

  // Performance metrics for active exam
  const activePapers = currentActiveExam?.papers ?? [];
  // Papers with a score only — an absent or not-yet-marked paper was not evaluated.
  const subjectsCount = activePapers.filter(
    (paper) => !paper.isAbsent && paper.marksObtained !== null,
  ).length;
  // Null when nothing in this exam was scored — shown as a dash, never as 0%.
  const overallPct = currentActiveExam?.percentage ?? null;
  const overallPctText = overallPct === null ? '—' : `${overallPct}%`;
  const performanceGrade = currentActiveExam?.gradeInfo ?? null;

  // Dynamic Class Rank
  const rankInfo = results?.ranks?.[currentActiveExam?.name ?? ''];
  // Null until the class has been ranked on this exam — shown as a dash, never a guess.
  const rankNumber = rankInfo?.rank ?? null;
  const totalStudents = rankInfo?.totalStudents ?? null;

  // What the subject teachers wrote against this exam's papers.
  const teacherRemarks = useMemo(() => remarksOf(currentActiveExam), [currentActiveExam]);

  const displayClass = className || results?.section?.displayName || 'Class';
  const childFirstName = childName?.trim().split(' ')[0] ?? '';


  return (
    <div className="flex flex-col gap-5 font-sans">
      {/* 1. TOP HEADER BAR */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-ink">Exams &amp; Results</h1>
          <p className="mt-0.5 text-sm font-medium text-slate-500 dark:text-slate-400">
            View your child's exam schedule, marks and detailed performance reports.
          </p>
        </div>

        {/* Right side: Class badge & Today's Date */}
        <div className="flex flex-wrap items-center gap-2.5 sm:self-start">
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-surface-2 border border-line shadow-2xs text-xs font-semibold text-ink">
            <GraduationCap className="h-4 w-4 text-accent shrink-0" />
            <span className="text-ink-2 text-base">Class:</span>
            <span className="text-ink font-bold text-sm">{displayClass}</span>
            {results?.classTeacher && (
              <span className="text-ink-2 font-medium ml-1 text-sm">
                • {results.classTeacher}
              </span>
            )}
          </div>

          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-surface-2 border border-line shadow-2xs text-sm font-semibold text-ink-2">
            <Calendar className="h-3.5 w-3.5 text-accent shrink-0" />
            <span>{todayFormatted}</span>
          </div>
        </div>
      </div>

      {/* 2. TABS & TERM SELECTION BAR */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        {/* Tabs: Upcoming Exams, Results, Report Cards */}
        <div className="inline-flex items-center  rounded-xl bg-surface-2 border border-line shadow-2xs">
          <button
            type="button"
            onClick={() => setActiveTab('upcoming')}
            className={cn(
              'px-5 py-2 rounded-lg text-base font-semibold transition-all cursor-pointer',
              activeTab === 'upcoming'
                ? 'bg-accent text-white shadow-2xs font-bold'
                : 'text-ink-2 hover:text-ink',
            )}
          >
            Upcoming Exams
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('results')}
            className={cn(
              'px-5 py-2 rounded-lg text-base font-semibold transition-all cursor-pointer',
              activeTab === 'results'
                ? 'bg-accent text-white shadow-2xs font-bold'
                : 'text-ink-2 hover:text-ink',
            )}
          >
            Results
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('reportCards')}
            className={cn(
              'px-5 py-2 rounded-lg text-base font-semibold transition-all cursor-pointer',
              activeTab === 'reportCards'
                ? 'bg-accent text-white shadow-2xs font-bold'
                : 'text-ink-2 hover:text-ink',
            )}
          >
            Report Cards
          </button>
        </div>

        {/* Right: which exam the results below are for. Only the exams that
            have marks — this product has no terms to choose between. */}
        {examGroups.length > 1 && currentActiveExam && (
          <div className="relative" ref={termRef}>
            <button
              type="button"
              aria-haspopup="listbox"
              aria-expanded={isTermOpen}
              onClick={() => setIsTermOpen((prev) => !prev)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-surface border border-line hover:border-accent-line text-sm font-bold text-ink shadow-2xs transition-colors cursor-pointer"
            >
              <span>{currentActiveExam.name}</span>
              <ChevronDown
                className={cn(
                  'h-3.5 w-3.5 text-ink-3 shrink-0 transition-transform duration-200',
                  isTermOpen && 'rotate-180',
                )}
              />
            </button>

            {isTermOpen && (
              <div
                role="listbox"
                className="absolute right-0 top-full mt-1.5 z-50 w-64 rounded-xl bg-surface border border-line shadow-xl py-1 animate-fade-in"
              >
                {examGroups.map((exam) => {
                  const isSelected = exam.name === currentActiveExam.name;
                  return (
                    <button
                      key={exam.name}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => {
                        setActiveExamName(exam.name);
                        setActiveTab('results');
                        setIsTermOpen(false);
                      }}
                      className={cn(
                        'w-full text-left px-3.5 py-2 text-sm font-semibold transition-colors cursor-pointer flex items-center justify-between',
                        isSelected
                          ? 'bg-accent-soft text-accent font-bold'
                          : 'text-ink-2 hover:bg-surface-2 hover:text-ink',
                      )}
                    >
                      <span>{exam.name}</span>
                      {isSelected && <span className="h-1.5 w-1.5 rounded-full bg-accent" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {isLoading && <SkeletonLines count={8} />}

      {!isLoading && error && (
        <div className="flex flex-col items-start gap-3 py-6">
          <Alert
            tone="critical"
            title={childName ? `Could not load ${childName}'s results` : 'Could not load results'}
          >
            {error.message}
          </Alert>
          <Button variant="secondary" size="sm" onClick={onRetry}>
            Try again
          </Button>
        </div>
      )}

      {/* Sat but not marked yet — on neither the scorecard below nor Upcoming Exams. */}
      {!isLoading && !error && activeTab === 'results' && (
        <AwaitingResultsList
          papers={results?.awaiting ?? []}
          whose={childName ? `${childName}'s` : "Your child's"}
        />
      )}

      {!isLoading && !error && marksList.length === 0 && activeTab === 'results' && (
        <div className="rounded-2xl border border-line bg-surface p-12 text-center shadow-2xs">
          <FileText className="h-10 w-10 text-ink-3 mx-auto mb-3" />
          <h3 className="text-base font-bold text-ink">No exam marks published yet</h3>
          <p className="mt-1 text-xs text-ink-2 max-w-md mx-auto">
            Exam scores and performance evaluations will appear here as soon as teachers record
            and publish the results for {childName || 'your child'}'s section.
          </p>
        </div>
      )}

      {/* --- TAB 1: RESULTS DASHBOARD (FIGMA SCREENSHOT DESIGN) --- */}
      {!isLoading && !error && marksList.length > 0 && activeTab === 'results' && (
        <>
          {/* 3. METRIC SUMMARY TILES (4 CARDS - SAME DESIGN SYSTEM AS HOMEWORK & ATTENDANCE) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {/* Tile 1: Subjects (Purple Variant) */}
            <div className="group relative block overflow-hidden rounded-2xl sm:rounded-3xl border border-purple-200/90 dark:border-slate-800 bg-purple-50/90 dark:bg-slate-900 p-3.5 sm:p-5 shadow-xs hover:shadow-md transition-[transform,box-shadow,border-color] duration-200">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-bold text-purple-800 dark:text-purple-400 leading-tight">
                    Subjects
                  </p>
                  <p className="mt-0.5 text-2xl font-black text-purple-600 dark:text-purple-400 tracking-tight tabular-nums truncate">
                    {subjectsCount}
                  </p>
                  <p className="mt-0.5 sm:mt-1 text-[11px] sm:text-xs font-semibold text-purple-600/90 dark:text-slate-400">
                    Total Evaluated
                  </p>
                </div>
                <span
                  className="grid h-9 w-9 sm:h-11 sm:w-11 shrink-0 place-items-center rounded-xl sm:rounded-2xl font-extrabold shadow-2xs text-white"
                  style={{ backgroundColor: '#7c3aed' }}
                  aria-hidden
                >
                  <FileText className="h-5 w-5" />
                </span>
              </div>
            </div>

            {/* Tile 2: Overall Percentage (Amber Variant) */}
            <div className="group relative block overflow-hidden rounded-2xl sm:rounded-3xl border border-amber-200/90 dark:border-slate-800 bg-amber-50/90 dark:bg-slate-900 p-3.5 sm:p-5 shadow-xs hover:shadow-md transition-[transform,box-shadow,border-color] duration-200">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-bold text-amber-800 dark:text-amber-400 leading-tight">
                    Overall Percentage
                  </p>
                  <p className="mt-0.5 text-2xl font-black text-amber-600 dark:text-amber-400 tracking-tight tabular-nums truncate">
                    {overallPctText}
                  </p>
                  <p className="mt-0.5 sm:mt-1 text-[11px] sm:text-xs font-semibold text-amber-700/90 dark:text-slate-400">
                    Aggregate Score
                  </p>
                </div>
                <span
                  className="grid h-9 w-9 sm:h-11 sm:w-11 shrink-0 place-items-center rounded-xl sm:rounded-2xl font-extrabold shadow-2xs text-white"
                  style={{ backgroundColor: '#d97706' }}
                  aria-hidden
                >
                  <Trophy className="h-5 w-5" />
                </span>
              </div>
            </div>

            {/* Tile 3: Class Rank (Cyan Variant) */}
            <div className="group relative block overflow-hidden rounded-2xl sm:rounded-3xl border border-cyan-200/90 dark:border-slate-800 bg-cyan-50/90 dark:bg-slate-900 p-3.5 sm:p-5 shadow-xs hover:shadow-md transition-[transform,box-shadow,border-color] duration-200">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-bold text-cyan-800 dark:text-cyan-400 leading-tight">
                    Class Rank
                  </p>
                  <div className="mt-0.5 flex items-baseline gap-1 truncate">
                    <span className="text-2xl font-black text-cyan-600 dark:text-cyan-400 tracking-tight tabular-nums">
                      {rankNumber ?? '—'}
                    </span>
                    {totalStudents !== null && (
                      <span className="text-base font-bold text-cyan-800/70 dark:text-slate-400">
                        / {totalStudents}
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 sm:mt-1 text-[11px] sm:text-xs font-semibold text-cyan-600/90 dark:text-slate-400">
                    In Class
                  </p>
                </div>
                <span
                  className="grid h-9 w-9 sm:h-11 sm:w-11 shrink-0 place-items-center rounded-xl sm:rounded-2xl font-extrabold shadow-2xs text-white"
                  style={{ backgroundColor: '#0891b2' }}
                  aria-hidden
                >
                  <BarChart3 className="h-5 w-5" />
                </span>
              </div>
            </div>

            {/* Tile 4: Performance (Emerald Variant) */}
            <div className="group relative block overflow-hidden rounded-2xl sm:rounded-3xl border border-emerald-200/90 dark:border-slate-800 bg-emerald-50/90 dark:bg-slate-900 p-3.5 sm:p-5 shadow-xs hover:shadow-md transition-[transform,box-shadow,border-color] duration-200">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-bold text-emerald-800 dark:text-emerald-400 leading-tight">
                    Performance
                  </p>
                  {/* The band's own colour — green under "Below expectation" would cheer a poor result. */}
                  <p
                    className={cn(
                      'mt-0.5 text-2xl font-black tracking-tight tabular-nums truncate',
                      performanceGrade?.color ?? 'text-ink-3',
                    )}
                  >
                    {performanceGrade?.label ?? '—'}
                  </p>
                  {/* Praise only under a grade that earns it — never under "Below expectation". */}
                  {isPraiseworthy(performanceGrade) && (
                    <p className="mt-0.5 sm:mt-1 text-[11px] sm:text-xs font-semibold text-emerald-700/90 dark:text-slate-400">
                      Keep it up!
                    </p>
                  )}
                </div>
                <span
                  className="grid h-9 w-9 sm:h-11 sm:w-11 shrink-0 place-items-center rounded-xl sm:rounded-2xl font-extrabold shadow-2xs text-white"
                  style={{ backgroundColor: '#059669' }}
                  aria-hidden
                >
                  <Smile className="h-5 w-5" />
                </span>
              </div>
            </div>
          </div>

          {/* 4. MAIN TWO-COLUMN LAYOUT */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
            {/* --- LEFT COLUMN: EXAM ACCORDION CARDS --- */}
            <div className="lg:col-span-7 flex flex-col gap-4">
              {/* EXPANDED ACTIVE EXAM CARD */}
              {currentActiveExam && (
                <div className="rounded-2xl border border-line bg-surface p-5 shadow-2xs flex flex-col gap-4">
                  {/* Card Header */}
                  <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-line">
                    <div>
                      <div className="flex items-center gap-2.5">
                        <h3 className="text-base font-bold text-ink">{currentActiveExam.name}</h3>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30">
                          Published
                        </span>
                      </div>
                      <p className="mt-0.5 text-sm text-ink-3 font-medium">
                        {currentActiveExam.dateRange}
                      </p>
                    </div>

                    {/* This exam's report card alone — "Save as PDF" in the print dialog downloads it. */}
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        setPrinting({
                          group: currentActiveExam,
                          rank: rankInfo ?? null,
                          childName,
                          admissionNumber: results?.child.admissionNumber ?? null,
                          displayClass,
                          academicYearName,
                          classTeacher: results?.classTeacher ?? null,
                        })
                      }
                      className="inline-flex items-center gap-2 rounded-xl text-xs font-bold shadow-2xs cursor-pointer border border-line hover:border-accent-line text-ink hover:text-accent"
                    >
                      <Printer className="h-3.5 w-3.5 text-accent" />
                      <span className="text-sm ">Print Report Card</span>
                    </Button>
                  </div>

                  {/* Marks Table */}
                  <div className="overflow-x-auto rounded-xl border border-line">
                    <table className="w-full border-collapse text-left text-xs">
                      <thead>
                        <tr className="border-b border-line bg-surface-2">
                          <th className="py-2.5 px-3 text-center w-10 text-xs font-bold text-ink-3">
                            #
                          </th>
                          <th className="py-2.5 px-3 text-sm font-bold text-ink">Subject</th>
                          <th className="py-2.5 px-3 text-right text-sm font-bold text-ink-2">
                            Max Marks
                          </th>
                          <th className="py-2.5 px-3 text-right text-sm font-bold text-ink">
                            Marks Obtained
                          </th>
                          <th className="py-2.5 px-3 text-right text-sm font-bold text-ink-2">
                            Percentage
                          </th>
                          <th className="py-2.5 px-3 text-center text-sm font-bold text-ink">
                            Grade
                          </th>
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-line">
                        {currentActiveExam.papers.map((paper, idx) => {
                          const max = paper.maxMarks;
                          const obtained = paper.marksObtained;
                          const pct =
                            !paper.isAbsent && obtained !== null && max > 0
                              ? Math.round((obtained / max) * 100)
                              : null;
                          const grade = paperGrade(paper);

                          return (
                            <tr
                              key={paper.uuid || idx}
                              className="hover:bg-surface-2/40 transition-colors"
                            >
                              <td className="py-2.5 px-3 text-center text-xs font-semibold text-ink-3">
                                {idx + 1}
                              </td>
                              <td className="py-2.5 px-3 font-bold text-sm text-ink">
                                {paper.subject.name}
                              </td>
                              <td className="py-2.5 px-3 text-right text-sm font-medium text-ink-2">
                                {max}
                              </td>
                              <td className="py-2.5 px-3 text-right text-sm font-bold text-ink">
                                {paper.isAbsent ? (
                                  <span className="text-rose-500 font-bold">Absent</span>
                                ) : obtained !== null ? (
                                  obtained
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td className="py-2.5 px-3 text-right text-sm font-semibold text-ink-2">
                                {pct === null ? '—' : `${pct}%`}
                              </td>
                              <td className="py-2.5 px-3 text-center">
                                {/* Absence is not a grade — "AB", as the printed card writes it. */}
                                <span
                                  className={cn(
                                    'inline-block px-2 py-0.5 rounded-md text-sm font-bold border',
                                    grade?.badgeClass ?? 'bg-surface-2 text-ink-2 border-line',
                                  )}
                                >
                                  {paper.isAbsent ? 'AB' : (grade?.grade ?? '—')}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Final Result, in the exam grade's own tone — a failing exam is not drawn as a win. */}
                  <div
                    className={cn(
                      'rounded-xl border px-4 py-3 flex flex-wrap items-center justify-between gap-3',
                      gradeToneClasses(currentActiveExam.gradeInfo).badge,
                    )}
                  >
                    <div className="flex items-center gap-2.5">
                      <Trophy className="h-5 w-5 shrink-0" />
                      <span className="text-sm font-bold">Final Result</span>
                    </div>

                    <div className="flex items-center gap-4 text-sm font-bold">
                      <span>
                        {currentActiveExam.percentage === null
                          ? '—'
                          : `${currentActiveExam.totalObtained} / ${currentActiveExam.totalMax}`}
                      </span>
                      <span aria-hidden>•</span>
                      <span>{overallPctText}</span>
                      <span aria-hidden>•</span>
                      <span>Grade: {currentActiveExam.gradeInfo?.grade ?? '—'}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* OTHER COLLAPSED EXAM ACCORDION ITEMS (FIGMA DESIGN) */}
              {accordionTerms.map((exam) => {
                const isExpanded = expandedAccordionTerm === exam.name;

                return (
                  <div
                    key={exam.name}
                    className="rounded-2xl border border-line bg-surface p-4 sm:p-5 shadow-2xs hover:border-accent-line transition-all"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedAccordionTerm((prev) => (prev === exam.name ? null : exam.name))
                      }
                      className="w-full text-left flex items-center justify-between gap-4 cursor-pointer select-none"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-xl bg-purple-500/15 border border-purple-500/30 flex items-center justify-center shrink-0">
                          <FileText className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div>
                          <h4 className="text-base font-bold text-slate-800">{exam.name}</h4>
                          <p className="mt-0.5 text-xs text-ink-3 font-medium">{exam.dateRange}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2.5">
                        {/* Only published exams reach this screen; a missed paper shows as Absent in its own row. */}
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30">
                          Published
                        </span>
                        <ChevronDown
                          className={cn(
                            'h-4 w-4 text-ink-3 shrink-0 ml-1 transition-transform duration-200',
                            isExpanded && 'rotate-180',
                          )}
                        />
                      </div>
                    </button>

                    {isExpanded && (
                      <div className="pt-3.5 mt-3.5 border-t border-line text-xs text-ink-2 animate-fade-in">
                        {exam.papers && exam.papers.length > 0 ? (
                          <div className="flex flex-col gap-2 mt-2">
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-ink text-xs">Paper Scores</span>
                              <button
                                type="button"
                                onClick={() => setActiveExamName(exam.name)}
                                className="text-xs font-bold text-accent hover:underline cursor-pointer"
                              >
                                View Detailed Analysis &rarr;
                              </button>
                            </div>
                            <div className="overflow-x-auto rounded-xl border border-line">
                            <table className="w-full border-collapse text-left text-xs">
                              <thead>
                                <tr className="border-b border-line bg-surface-2">
                                  <th className="py-2 px-3 text-center w-10 text-xs font-bold text-ink-3">#</th>
                                  <th className="py-2 px-3 text-xs font-bold text-ink">Subject</th>
                                  <th className="py-2 px-3 text-right text-xs font-bold text-ink-2">Max Marks</th>
                                  <th className="py-2 px-3 text-right text-xs font-bold text-ink">Marks Obtained</th>
                                  <th className="py-2 px-3 text-center text-xs font-bold text-ink">Grade</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-line">
                                {exam.papers.map((paper, idx) => {
                                  const grade = paperGrade(paper);
                                  return (
                                    <tr key={paper.uuid || idx} className="hover:bg-surface-2/40">
                                      <td className="py-2 px-3 text-center text-xs font-semibold text-ink-3">{idx + 1}</td>
                                      <td className="py-2 px-3 font-bold text-xs text-ink">{paper.subject.name}</td>
                                      <td className="py-2 px-3 text-right text-xs font-medium text-ink-2">{paper.maxMarks}</td>
                                      <td className="py-2 px-3 text-right text-xs font-bold text-ink">{paper.isAbsent ? 'Absent' : paper.marksObtained ?? '—'}</td>
                                      <td className="py-2 px-3 text-center">
                                        <span className={cn('inline-block px-2 py-0.5 rounded-md text-xs font-bold border', grade?.badgeClass ?? 'bg-surface-2 text-ink-2 border-line')}>
                                          {paper.isAbsent ? 'AB' : (grade?.grade ?? '—')}
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* --- RIGHT COLUMN: PERFORMANCE CHART, GRADE SCALE, REMARKS --- */}
            <div className="lg:col-span-5 flex flex-col gap-4">
              {/* CARD 1: SUBJECT-WISE PERFORMANCE (BAR CHART) */}
              <div className="rounded-2xl border border-line bg-surface p-5 shadow-2xs flex flex-col gap-4">
                <div className="flex items-center justify-between pb-3 border-b border-line">
                  <h3 className="text-base font-bold text-ink">Subject-wise Performance</h3>
                  <span className="text-sm font-bold text-accent bg-accent-soft border border-accent-line px-3 py-0.5 rounded-full">
                    Overall: {overallPctText}
                  </span>
                </div>

                {/* Vertical Bar Chart */}
                <div className="pt-2">
                  <div className="h-44 flex items-end justify-between gap-2 border-b border-line px-1">
                    {activePapers.map((paper, idx) => {
                      const max = paper.maxMarks;
                      const obtained = paper.marksObtained;
                      const pct =
                        !paper.isAbsent && obtained !== null && max > 0
                          ? Math.round((obtained / max) * 100)
                          : null;
                      const barBg = getSubjectChartColor(idx);

                      return (
                        <div
                          key={paper.uuid || idx}
                          className="flex-1 flex flex-col items-center justify-end h-full group"
                        >
                          <span className="text-xs font-bold text-ink-2 mb-1.5 opacity-90 group-hover:scale-110 transition-transform">
                            {paper.isAbsent ? 'AB' : pct === null ? '—' : `${pct}%`}
                          </span>
                          {/* No bar without a score — the 8% floor would draw a result nobody got. */}
                          {pct !== null && (
                            <div
                              style={{ height: `${Math.max(8, pct)}%` }}
                              className={cn(
                                'w-full max-w-[32px] rounded-t-lg transition-all shadow-2xs group-hover:opacity-90',
                                barBg,
                              )}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Subject Name Labels below X-Axis */}
                  <div className="flex items-start justify-between gap-2 pt-2 px-1">
                    {activePapers.map((paper, idx) => (
                      <span
                        key={paper.uuid || idx}
                        title={paper.subject.name}
                        className="flex-1 text-center text-2xs font-bold text-ink-3 truncate max-w-[48px]"
                      >
                        {paper.subject.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* CARD 2: GRADE SCALE */}
              <div className="rounded-2xl border border-line bg-surface p-5 shadow-2xs flex flex-col gap-3">
                <h3 className="text-base font-bold text-ink">Grade Scale</h3>

                {/* The shared scale the profile screen grades on — one source, so the two never disagree. */}
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-center">
                  {GRADE_SCALE.map(({ band, range }, index) => (
                    <div
                      key={band.grade}
                      className={cn(
                        'group/tooltip relative rounded-xl border p-2 flex flex-col items-center justify-center gap-0.5 cursor-pointer transition-all hover:shadow-xs',
                        band.badgeClass,
                      )}
                    >
                      <span className="text-sm font-extrabold leading-tight">{band.grade}</span>
                      <span className="text-xs font-semibold text-ink-2">{range}</span>
                      <span className="text-[9px] font-bold text-ink-2 leading-tight truncate max-w-full">
                        {band.label}
                      </span>

                      {/* Tooltip on hover */}
                      <div
                        role="tooltip"
                        className={cn(
                          'pointer-events-none absolute bottom-[calc(100%+6px)] opacity-0 group-hover/tooltip:opacity-100 transition-opacity duration-150 z-30 px-2 py-1 text-2xs font-bold text-white bg-slate-900 dark:bg-slate-800 rounded-md shadow-md whitespace-nowrap border border-slate-700/50',
                          index === GRADE_SCALE.length - 1
                            ? 'right-0 translate-x-0'
                            : index === 0
                            ? 'left-0 translate-x-0'
                            : 'left-1/2 -translate-x-1/2',
                        )}
                      >
                        {band.label}
                        <div
                          className={cn(
                            'absolute top-full -mt-0.5 border-4 border-transparent border-t-slate-900 dark:border-t-slate-800',
                            index === GRADE_SCALE.length - 1
                              ? 'right-4'
                              : index === 0
                              ? 'left-4'
                              : 'left-1/2 -translate-x-1/2',
                          )}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* CARD 3: TEACHER'S REMARKS */}
              <div className="rounded-2xl border border-line bg-surface p-5 shadow-2xs flex flex-col gap-3.5">
                <div className="flex items-center gap-2.5">
                  <div className="h-8 w-8 rounded-full bg-purple-500/15 border border-purple-500/30 flex items-center justify-center shrink-0">
                    <MessageSquare className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  <h3 className="text-base font-bold text-ink">Teacher's Remarks</h3>
                </div>

                {teacherRemarks.length === 0 ? (
                  <p className="text-sm text-ink-3">
                    No teacher has written a remark on {currentActiveExam?.name ?? 'this exam'} yet.
                  </p>
                ) : (
                  <ul className="flex flex-col gap-3">
                    {teacherRemarks.map((item) => (
                      <li
                        key={item.subject}
                        className="rounded-2xl border border-line bg-surface-2/40 p-4 relative overflow-hidden"
                      >
                        <Quote className="h-6 w-6 text-ink-3/25 dark:text-ink-3/35 absolute top-3 right-3 pointer-events-none select-none" />
                        <p className="text-sm text-ink-2 leading-relaxed pr-8">
                          &ldquo;{item.remark}&rdquo;
                        </p>
                        {/* Signed by whoever entered the mark; unsigned when nobody is recorded. */}
                        {item.author && (
                          <p className="mt-2 text-xs font-semibold text-ink">— {item.author}</p>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* --- TAB 2: UPCOMING EXAMS SCHEDULE VIEW (FIGMA DESIGN) --- */}
      {!isLoading && !error && activeTab === 'upcoming' && (
        <ParentUpcomingExamsTab childUuid={results?.child.uuid} />
      )}

      {/* --- TAB 3: REPORT CARDS DASHBOARD (FIGMA DESIGN) --- */}
      {!isLoading && !error && activeTab === 'reportCards' && (
        <ParentReportCardsTab
          examGroups={examGroups}
          ranks={results?.ranks ?? {}}
          childName={childName}
          admissionNumber={results?.child.admissionNumber ?? null}
          childFirstName={childFirstName}
          displayClass={displayClass}
          academicYearName={academicYearName}
          classTeacher={results?.classTeacher ?? null}
        />
      )}

      <ReportCardPrint target={printing} onFinished={() => setPrinting(null)} />
    </div>
  );
}